import { Injectable } from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { BehaviorSubject } from "rxjs";
import { CheckWinningsRequestStatus } from "./check-winnings-request-status";

export interface CheckWinningsFlowTelemetry {
  userId?: string;
  claimEventId?: string;
  source?: string;
  outcomeStatus?: CheckWinningsRequestStatus | "technical_error" | "cancelled";
  latencyMs?: number;
  retryCount?: number;
  memberFacingMessage?: string;
}

@Injectable({
  providedIn: "root",
})
export class CheckWinningsFlowService {
  private inFlightSubject = new BehaviorSubject<boolean>(false);
  readonly isInFlight$ = this.inFlightSubject.asObservable();

  private retryCount = 0;
  private checkStartTime: number | null = null;
  private activeClaimEventId: string | null = null;
  private activeCheckNo: number | string | null = null;
  private activeCheckPrize: string | null = null;

  constructor(private analytics: AngularFireAnalytics) {}

  isInFlight(): boolean {
    return this.inFlightSubject.value;
  }

  getActiveClaimEventId(): string | null {
    return this.activeClaimEventId;
  }

  getActiveCheckNo(): number | string | null {
    return this.activeCheckNo;
  }

  getActiveCheckPrize(): string | null {
    return this.activeCheckPrize;
  }

  startCheck(telemetry: Pick<CheckWinningsFlowTelemetry, "userId" | "source">): boolean {
    if (this.inFlightSubject.value) {
      return false;
    }

    // Fresh flows start at zero; splash retries call registerRetry() first and keep the count.
    if (telemetry.source !== "splash_retry") {
      this.retryCount = 0;
    }

    this.inFlightSubject.next(true);
    this.checkStartTime = Date.now();
    this.logFlowEvent("check_winnings_click", telemetry);
    return true;
  }

  trackRequestStart(
    claimEventId: string,
    userId?: string,
    checkNo?: number | string | null,
    checkPrize?: string | null
  ): void {
    this.activeClaimEventId = claimEventId;
    this.activeCheckNo = checkNo ?? null;
    this.activeCheckPrize = checkPrize ?? null;
    this.logFlowEvent("check_winnings_request_start", {
      userId,
      claimEventId,
      retryCount: this.retryCount,
    });
  }

  completeCheck(telemetry: CheckWinningsFlowTelemetry): void {
    const latencyMs =
      telemetry.latencyMs ??
      (this.checkStartTime != null ? Date.now() - this.checkStartTime : undefined);

    this.logFlowEvent("check_winnings_outcome", {
      ...telemetry,
      claimEventId: telemetry.claimEventId ?? this.activeClaimEventId ?? undefined,
      latencyMs,
      retryCount: telemetry.retryCount ?? this.retryCount,
    });

    this.inFlightSubject.next(false);
    this.checkStartTime = null;
    this.activeClaimEventId = null;
    this.activeCheckNo = null;
    this.activeCheckPrize = null;
  }

  registerRetry(): void {
    this.retryCount += 1;
  }

  resetRetryCount(): void {
    this.retryCount = 0;
  }

  private logFlowEvent(eventName: string, payload: CheckWinningsFlowTelemetry): void {
    this.analytics.logEvent(eventName, {
      user_id: payload.userId ?? null,
      claim_event_id: payload.claimEventId ?? null,
      source: payload.source ?? null,
      outcome_status: payload.outcomeStatus ?? null,
      latency_ms: payload.latencyMs ?? null,
      retry_count: payload.retryCount ?? this.retryCount,
      member_facing_message: payload.memberFacingMessage ?? null,
    });
  }
}
